import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { motion } from "framer-motion";
import { CheckCircle, Lock, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const cardElementOptions = {
  style: {
    base: {
      fontSize: "14px",
      color: "#fff",
      "::placeholder": { color: "#666" },
      fontFamily: "ui-monospace, SFMono-Regular, monospace",
      iconColor: "#007AFF",
    },
    invalid: { color: "#FF3B30", iconColor: "#FF3B30" },
  },
};

function SignupInner() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addrLine1, setAddrLine1] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!stripe || !elements) {
      setErr("Payment form is still loading. Try again in a second.");
      return;
    }
    if (!email || !password || !name || !phone || !addrLine1 || !addrCity || !addrState || !addrPostal) {
      setErr("All fields including your full mailing address are required.");
      return;
    }
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      // Create a PaymentMethod from the card details (does NOT charge the card).
      const card = elements.getElement(CardElement);
      const { error: pmErr, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: {
          name: name.trim(), email: email.trim(), phone: phone.trim(),
          address: {
            line1: addrLine1.trim(),
            city: addrCity.trim(),
            state: addrState.trim(),
            postal_code: addrPostal.trim(),
          },
        },
      });
      if (pmErr) {
        setErr(pmErr.message || "Card couldn't be processed.");
        setSubmitting(false);
        return;
      }
      // Send to backend — backend creates user, customer, attaches PM, creates trial sub.
      const promo_variant = (() => {
        try { return window.localStorage.getItem("hot.variant") || undefined; } catch { return undefined; }
      })();
      const { data } = await api.post("/billing/signup", {
        email: email.trim(),
        password,
        name: name.trim(),
        phone: phone.trim(),
        address_line1: addrLine1.trim(),
        address_city: addrCity.trim(),
        address_state: addrState.trim(),
        address_postal: addrPostal.trim(),
        payment_method_id: paymentMethod.id,
        promo_variant,
      });
      if (typeof refresh === "function") await refresh();
      toast.success("Welcome aboard. Your 7-day free trial just started.");
      navigate("/sports/mlb");
      // Keep the small dot of UX clarity:
      // eslint-disable-next-line no-console
      console.info("Trial ends:", data?.trial_ends_at);
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || e2.message;
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <Link to="/sports/mlb" className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white" data-testid="signup-back">← Back to picks</Link>
      <div className="mt-6 mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Start your trial</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">7 DAYS FREE.</h1>
        <p className="text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          We require a card on file so your weekly subscription kicks in automatically on Monday after your trial ends.
          Cancel anytime from your account.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 bg-[#22C55E]/10 border border-[#22C55E]/40 rounded-sm px-4 py-2.5" data-testid="no-charge-banner">
          <Lock size={14} weight="fill" className="text-[#22C55E]" />
          <span className="text-sm sm:text-base">
            <strong className="text-[#22C55E] font-bold">Your card will NOT be charged for 7 days.</strong>
            <span className="text-zinc-300"> First $15 weekly charge fires the Monday after trial ends.</span>
          </span>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="signup-form">
        {/* Account info card */}
        <div className="lg:col-span-3 bg-[#121212] border border-white/10 rounded-sm p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF]">Account</div>

          <Field label="Full Name" testid="signup-name" value={name} onChange={setName} required maxLength={120} placeholder="Jane Doe" />
          <Field label="Email" testid="signup-email" value={email} onChange={setEmail} type="email" required placeholder="you@example.com" />
          <Field label="Phone" testid="signup-phone" value={phone} onChange={setPhone} type="tel" required maxLength={40} placeholder="+1 555 123 4567" />
          <Field label="Password" testid="signup-password" value={password} onChange={setPassword} type="password" required minLength={6} placeholder="At least 6 characters" />

          <div className="pt-2 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF] mb-3 mt-3">Mailing Address</div>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              Required to verify your one free trial per household. Used only for fraud prevention — never shared.
            </p>
            <Field label="Street Address" testid="signup-addr1" value={addrLine1} onChange={setAddrLine1} required maxLength={200} placeholder="123 Main St, Apt 4B" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="City" testid="signup-city" value={addrCity} onChange={setAddrCity} required maxLength={100} placeholder="Chicago" />
              <Field label="State / Region" testid="signup-state" value={addrState} onChange={setAddrState} required maxLength={60} placeholder="IL" />
            </div>
            <div className="mt-3">
              <Field label="ZIP / Postal Code" testid="signup-postal" value={addrPostal} onChange={setAddrPostal} required maxLength={20} placeholder="60601" />
            </div>
          </div>

          <div className="pt-4 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">Card on file</div>
            <div className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-3.5" data-testid="signup-card-element">
              <CardElement options={cardElementOptions} />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[#22C55E] flex items-center gap-1.5 font-bold">
              <Lock size={10} weight="fill" /> Secured by Stripe · Card NOT charged for 7 days
            </div>
          </div>

          {err && (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-sm px-3 py-2 text-sm text-[#FF3B30]" data-testid="signup-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !stripe}
            data-testid="signup-submit"
            className="w-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-black font-bold uppercase tracking-[0.2em] py-3.5 rounded-sm text-xs transition-colors"
          >
            {submitting ? "Setting up your trial…" : "Start 7-day free trial"}
          </button>

          <div className="text-center text-xs text-zinc-500">
            Already have an account? <Link to="/login" className="text-[#007AFF] hover:underline">Sign in</Link>
          </div>
        </div>

        {/* Right rail summary */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-[#121212] border border-[#22C55E]/30 rounded-sm p-6 h-fit"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#22C55E] mb-3">
            <ShieldCheck size={14} weight="fill" /> What you&apos;re getting
          </div>
          <ul className="space-y-2.5 text-sm text-zinc-300 mb-6">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Every Charlie & Dakota pick, before first pitch</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Live DraftKings odds + stake amounts</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Tamper-evident locked-pick receipts</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" /> Email reminder on day 6</li>
          </ul>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400"><span>7-day trial</span><span className="text-[#22C55E] font-bold">FREE</span></div>
            <div className="flex justify-between text-zinc-400"><span>After trial</span><span>$15 / week</span></div>
            <div className="flex justify-between text-zinc-500 text-xs"><span>Billing day</span><span>Monday</span></div>
          </div>
          <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-zinc-600 leading-relaxed">
            Cancel anytime from your account. If payment fails, your subscription ends immediately — no retries.
          </div>
        </motion.div>
      </form>

      <div className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        21+ · Not financial advice · Gamble responsibly · 1-800-GAMBLER
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", ...rest }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors"
        {...rest}
      />
    </div>
  );
}

export default function Signup() {
  const [pk, setPk] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/config");
        if (cancelled) return;
        if (!data.configured || !data.publishable_key) {
          setErr("Billing is not configured. Please contact support.");
        } else {
          setPk(data.publishable_key);
        }
      } catch (e) {
        if (!cancelled) setErr("Couldn't load billing config. Try again.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stripePromise = useMemo(() => (pk ? loadStripe(pk) : null), [pk]);

  if (err) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-16 text-center" data-testid="signup-billing-not-configured">
        <h1 className="font-display text-4xl mb-3">Billing offline</h1>
        <p className="text-zinc-400">{err}</p>
      </div>
    );
  }
  if (!stripePromise) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-16 text-center text-zinc-500 font-mono-d" data-testid="signup-loading">
        Loading secure checkout…
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <SignupInner />
    </Elements>
  );
}
